import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import type { MonitoringAnalyticsRealtime } from '../../types/monitoring';

type Props = {
  data?: MonitoringAnalyticsRealtime | null;
  loading?: boolean;
  isFetching?: boolean;
  lastUpdateLabel?: string;
};

const GA_REALTIME_URL = 'https://analytics.google.com/analytics/web/#/p0/realtime';

function RowMetric({
  label,
  value,
}: {
  label: string;
  value: number;
}) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ py: 0.35 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, pr: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, flexShrink: 0 }}>
        {value}
      </Typography>
    </Stack>
  );
}

const MonitoringAnalyticsRealtimePanel: React.FC<Props> = ({
  data,
  loading = false,
  isFetching = false,
  lastUpdateLabel,
}) => {
  if (loading && !data) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </CardContent>
      </Card>
    );
  }

  const configured = data?.configured === true;
  const error = data?.error?.trim() || null;

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.75 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Visiteurs site (GA4)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Temps réel Google Analytics — utilisateurs ayant accepté les cookies analytics.
              Les villes géolocalisées apparaissent sur la carte (pastilles bleues).
            </Typography>
          </Box>
          <Chip
            size="small"
            color={isFetching ? 'primary' : 'default'}
            label={isFetching ? 'Sync…' : '60 s'}
            icon={isFetching ? <CircularProgress size={12} color="inherit" /> : undefined}
          />
        </Stack>

        {!configured ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Configurez <code>GA4_PROPERTY_ID</code> et <code>GA4_SERVICE_ACCOUNT_JSON</code> sur l&apos;API
            (compte de service avec accès lecture à la propriété GA4).
          </Alert>
        ) : null}

        {error ? <Alert severity="warning" sx={{ mb: 1.5 }}>{error}</Alert> : null}

        {configured ? (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'rgba(81, 190, 238, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#2b8fc7',
              }}
            >
              <PeopleOutlineIcon />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                {data?.active_users ?? 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                utilisateurs actifs (≈ 30 min)
              </Typography>
            </Box>
          </Stack>
        ) : null}

        {configured && (data?.cities?.length ?? 0) > 0 ? (
          <Box sx={{ mb: 1.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              Top villes
            </Typography>
            {data?.cities.slice(0, 8).map((row) => (
              <RowMetric
                key={`${row.city}-${row.country}`}
                label={[row.city, row.country].filter(Boolean).join(', ')}
                value={row.active_users}
              />
            ))}
          </Box>
        ) : null}

        {configured && (data?.pages?.length ?? 0) > 0 ? (
          <Box sx={{ mb: 1.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              Pages vues
            </Typography>
            {data?.pages.slice(0, 5).map((row) => (
              <RowMetric key={row.name} label={row.name} value={row.active_users} />
            ))}
          </Box>
        ) : null}

        {configured && (data?.devices?.length ?? 0) > 0 ? (
          <Box sx={{ mb: 1.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              Appareils
            </Typography>
            {data?.devices.map((row) => (
              <RowMetric key={row.device} label={row.device} value={row.active_users} />
            ))}
          </Box>
        ) : null}

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            {lastUpdateLabel ? `MAJ : ${lastUpdateLabel}` : ''}
          </Typography>
          <Button
            component={Link}
            href={GA_REALTIME_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ textTransform: 'none' }}
          >
            Ouvrir GA4
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MonitoringAnalyticsRealtimePanel;
